import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "veterinary-web-design-colorado",
  metaTitle: "Veterinary Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom veterinary web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Veterinary Web Design Colorado",
  eyebrow: "Veterinary Web Design — Colorado",
  h1: (
    <>
      Veterinary <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded veterinary websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Veterinary websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first veterinary sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Veterinary web design in Colorado",
    body: "Veterinary web design is the practice of building conversion-focused, mobile-first websites for veterinary businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — vet calls happen with a sick pet in the room." },
    { title: "Service pages per visit type", body: "Wellness, dental, surgery, emergency, exotic, end-of-life — each ranks independently." },
    { title: "Online booking + new client intake", body: "Real-time booking integration with PetDesk, Rapport, or your PIMS." },
    { title: "Sticky click-to-call", body: "Phone on every scroll — most vet calls are urgent." },
    { title: "Vet bio pages", body: "Each DVM with credentials, philosophy, and direct booking CTA." },
    { title: "Schema and SEO at launch", body: "VeterinaryCare, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Emergency intent dominates", body: "Sick or injured pet searches are now-searches. Speed wins the call." },
    { heading: "Service-type pages outrank generic vet pages", body: "Dental cleanings, surgery, and exotic care are different searches with different conversion flows." },
    { heading: "Trust and bedside manner close the new client", body: "Reviews, vet bios, and tour photos above the fold drive new-client acquisition." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a veterinary website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a veterinary website?", a: "Typical timeline is 4–6 weeks for a 12–20 page veterinary site with full service and service-area pages. Larger multi-location veterinary businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs veterinary businesses?", a: "No — we build veterinary sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Veterinary Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for veterinary businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/veterinary-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
